import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Grid } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import { useLanguage } from '../context/LanguageContext';

const initialCustomers = [
    { id: 1, name: 'Amit Sharma', phone: '9876543210', visits: 12, spent: 4850, loyaltyPoints: 480, lastVisit: '2 days ago' },
    { id: 2, name: 'Priya Patil', phone: '9012345678', visits: 8, spent: 3120, loyaltyPoints: 310, lastVisit: '5 days ago' },
    { id: 3, name: 'Rahul Deshmukh', phone: '8888888888', visits: 24, spent: 11450, loyaltyPoints: 1145, lastVisit: 'Today' }
];

export default function CustomerManagementView() {
    const { t, currentLanguage } = useLanguage();
    const [customers, setCustomers] = useState(initialCustomers);
    const [search, setSearch] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');

    const handleAddCustomer = () => {
        if (name && phone) {
            const newCust = {
                id: customers.length + 1,
                name,
                phone,
                visits: 1,
                spent: 0,
                loyaltyPoints: 0,
                lastVisit: 'Just Joined'
            };
            setCustomers([newCust, ...customers]);
            setName('');
            setPhone('');
        }
    };

    const handleWhatsAppTrigger = (cust) => {
        alert(`WhatsApp Receipt triggered successfully for ${cust.name} (${cust.phone})!`);
    };

    const filtered = customers.filter(c => 
        c.name.toLowerCase().includes(search.toLowerCase()) || 
        c.phone.includes(search)
    );

    return (
        <Box sx={{ p: 4, maxWidth: 1200, margin: '0 auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: '#008751' }}>
                {currentLanguage === 'mr' ? 'ग्राहक व्यवस्थापन (CRM)' : currentLanguage === 'hi' ? 'ग्राहक प्रबंधन (CRM)' : 'Customer Management Hub'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                {currentLanguage === 'mr'
                    ? 'ग्राहकांचे रेकॉर्ड, विझिट्स इतिहास, लॉयल्टी गुण आणि व्हॉट्सॲप बिलांचे व्यवस्थापन करा.'
                    : currentLanguage === 'hi'
                    ? 'ग्राहकों के रिकॉर्ड, विज़िट इतिहास, लॉयल्टी अंक और व्हाट्सएप बिल प्रबंधित करें।'
                    : 'Manage hotel guest histories, loyalty point balances, and trigger custom WhatsApp receipts.'}
            </Typography>

            <Grid container spacing={4} sx={{ mb: 4 }}>
                {/* Search Bar */}
                <Grid item xs={12} md={7}>
                    <Card sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SearchIcon sx={{ color: '#64748b' }} />
                        <TextField
                            placeholder={currentLanguage === 'mr' ? 'नाव किंवा फोन नंबर शोधा...' : currentLanguage === 'hi' ? 'नाम या फोन नंबर खोजें...' : 'Search guest name or mobile...'}
                            variant="standard"
                            fullWidth
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{ disableUnderline: true }}
                        />
                    </Card>
                </Grid>

                {/* Add New Customer Panel */}
                <Grid item xs={12} md={5}>
                    <Card sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: '#008751' }}>
                            {currentLanguage === 'mr' ? 'नवीन ग्राहक नोंदवा' : currentLanguage === 'hi' ? 'नया ग्राहक दर्ज करें' : 'Register New Guest'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField 
                                label="Name" 
                                size="small" 
                                value={name} 
                                onChange={(e) => setName(e.target.value)}
                                sx={{ flexGrow: 1 }}
                            />
                            <TextField 
                                label="Phone" 
                                size="small" 
                                value={phone} 
                                onChange={(e) => setPhone(e.target.value)}
                                sx={{ flexGrow: 1 }}
                            />
                            <Button 
                                variant="contained" 
                                onClick={handleAddCustomer}
                                sx={{ bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' } }}
                            >
                                <AddIcon />
                            </Button>
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Customers table */}
            <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#f0fdf4' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 800, color: '#008751' }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: '#008751' }}>Phone</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: '#008751' }}>Visits</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: '#008751' }}>Total Spent</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: '#008751' }}>Loyalty Points</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: '#008751' }}>Last Visit</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800, color: '#008751' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map((cust) => (
                            <TableRow key={cust.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar sx={{ bgcolor: '#e6f4ea', color: '#008751', fontWeight: 800 }}>
                                            {cust.name[0]}
                                        </Avatar>
                                        <Typography sx={{ fontWeight: 700 }}>{cust.name}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{cust.phone}</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>{cust.visits}</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#008751' }}>₹{cust.spent}</TableCell>
                                <TableCell>
                                    <Chip 
                                        label={`${cust.loyaltyPoints} pts`} 
                                        size="small" 
                                        sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 800 }} 
                                    />
                                </TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>{cust.lastVisit}</TableCell>
                                <TableCell align="center">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="success"
                                        startIcon={<WhatsAppIcon />}
                                        onClick={() => handleWhatsAppTrigger(cust)}
                                        sx={{ fontWeight: 700, borderRadius: 2 }}
                                    >
                                        WhatsApp Bill
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
